import type { Experiment } from 'types';
import { useState } from 'react';
import { Check, ChevronDown, FlaskConical } from 'lucide-react';
import { cn } from 'helpers/cn';
import { EXPERIMENT_STATUSES } from 'definitions';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FOCUS_RING } from '../../ConsolePanel';

type ExperimentPickerProps = {
  id?: string;
  experiments: Experiment[];
  experimentId: number | null;
  isLoading: boolean;
  onSelect: (experimentId: number | null) => void;
};

const TRIGGER =
  'border-line bg-field text-foreground hover:bg-hover flex h-9 w-full items-center gap-2 rounded-lg border px-3 text-left text-body ' +
  `transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING}`;

// Same tones the experiments library gives these statuses, so the same word never
// changes colour between screens. Anything unmapped falls back to the hairline chip.
const STATUS_TONE: Record<string, string> = {
  Running: 'border-ok-line text-ok',
  Deployed: 'border-accent-line text-accent',
  QA: 'border-sel-line text-sel',
};

const Chip = ({ tone, children }: { tone?: string; children: React.ReactNode }) => (
  <span className={cn('micro-label rounded-pill shrink-0 border px-2 py-0.5', tone ?? 'border-line text-dim')}>
    {children}
  </span>
);

/**
 * Which experiment a survey is tied to: a searchable list grouped by status, since
 * the status decides whether anyone sees the survey at all.
 *
 * Built on Popover and Command directly rather than the shared Combobox, which
 * cannot select anything while its value is empty, and an unlinked survey's is.
 */
export const ExperimentPicker = ({ id, experiments, experimentId, isLoading, onSelect }: ExperimentPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const linked = experiments.find((experiment) => experiment.id === experimentId);
  const groups = EXPERIMENT_STATUSES.map((status) => ({
    status,
    experiments: experiments.filter((experiment) => experiment.status === status),
  })).filter((group) => group.experiments.length > 0);

  const choose = (next: number | null) => {
    setIsOpen(false);
    onSelect(next);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger id={id} disabled={isLoading} className={TRIGGER}>
        <FlaskConical className="text-dim size-4 shrink-0" strokeWidth={1.5} />
        {isLoading ? (
          <span className="text-dim flex-1">Loading experiments...</span>
        ) : linked ? (
          <>
            <span className="min-w-0 flex-1 truncate font-medium">{linked.name}</span>
            <Chip tone={STATUS_TONE[linked.status]}>{linked.status}</Chip>
          </>
        ) : experimentId !== null ? (
          // A linked experiment that was since archived or deleted still shows, so the
          // field never claims a link it does not have.
          <>
            <span className="min-w-0 flex-1 truncate">Experiment {experimentId}</span>
            <Chip tone="border-danger-line text-danger">No longer available</Chip>
          </>
        ) : (
          <span className="text-dim flex-1">Not tied to an experiment</span>
        )}
        <ChevronDown className="text-dim size-4 shrink-0" strokeWidth={1.5} />
      </PopoverTrigger>
      <PopoverContent align="start" className="border-line bg-panel w-(--radix-popover-trigger-width) rounded-lg p-0">
        <Command>
          <CommandInput placeholder="Search experiments" className="text-body" />
          <CommandList className="max-h-80">
            <CommandEmpty className="supporting px-3 py-4">No experiments match.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="not tied to an experiment"
                onSelect={() => choose(null)}
                className="cursor-pointer gap-2 text-body"
              >
                <Check className={cn('size-3.5 shrink-0', experimentId === null ? 'opacity-100' : 'opacity-0')} />
                <span className="text-dim">Not tied to an experiment</span>
              </CommandItem>
            </CommandGroup>
            {groups.map((group) => (
              <CommandGroup key={group.status} heading={group.status}>
                {group.experiments.map((experiment) => (
                  <CommandItem
                    key={experiment.id}
                    /* cmdk filters on this, so it carries the name; the id keeps two
                       experiments with the same name apart. */
                    value={`${experiment.name} ${experiment.id}`}
                    onSelect={() => choose(experiment.id)}
                    className={cn(
                      'cursor-pointer gap-2 text-body',
                      experiment.id === experimentId && 'text-foreground font-medium',
                    )}
                  >
                    <Check
                      className={cn('size-3.5 shrink-0', experiment.id === experimentId ? 'opacity-100' : 'opacity-0')}
                    />
                    <span className="min-w-0 flex-1 truncate">{experiment.name}</span>
                    <span className="micro-label shrink-0 tracking-normal">
                      {experiment.variants?.length ?? 0} variants
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
